import React from "react";
import { Box, Slider, Typography } from "@mui/material";

interface DroughtFactorControlProps {
  /** McArthur drought factor, 0-10. */
  value: number;
  onChange: (value: number) => void;
  /** Fired when the user lets go of the slider (e.g. to refresh an FFDI readout). */
  onChangeCommitted?: () => void;
  /** Dark weather-panel styling, matching the surrounding sliders. */
  dark?: boolean;
}

const droughtFactorLabel = (value: number): string =>
  value < 5 ? "Low" : value < 7.5 ? "Moderate" : "Severe";

/**
 * Drought factor for a simulation: how dry the fine fuel is this week.
 *
 * Laid out as a column that fills its container, with the slider pinned to
 * the bottom, so it lines up with the curing and drought index controls
 * beside it whatever helper text those happen to show.
 */
const DroughtFactorControl: React.FC<DroughtFactorControlProps> = ({
  value,
  onChange,
  onChangeCommitted,
  dark = true,
}) => {
  const subtle = dark ? "rgba(255, 255, 255, 0.6)" : "text.secondary";
  const faint = dark ? "rgba(255, 255, 255, 0.5)" : "text.disabled";
  const strong = dark ? "#fff" : "text.primary";

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: subtle, textTransform: "uppercase", fontSize: "0.75rem" }}
          >
            Drought Factor
          </Typography>
          <Typography variant="h4" sx={{ color: strong, fontWeight: 600 }}>
            {value.toFixed(1)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="caption" sx={{ color: "#ff9800", fontWeight: 600 }}>
            {droughtFactorLabel(value)}
          </Typography>
          <Typography variant="caption" sx={{ color: faint, display: "block" }}>
            Fuel Dryness
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <Slider
          value={value}
          onChange={(_, next) => onChange(next as number)}
          onChangeCommitted={onChangeCommitted ? () => onChangeCommitted() : undefined}
          min={0}
          max={10}
          step={0.5}
          sx={{
            color: "#ff9800",
            "& .MuiSlider-thumb": { width: 20, height: 20 },
            "& .MuiSlider-track": { height: 6 },
            "& .MuiSlider-rail": {
              height: 6,
              bgcolor: dark ? "rgba(255, 255, 255, 0.1)" : undefined,
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="caption" sx={{ color: faint }}>0 (Wet)</Typography>
          <Typography variant="caption" sx={{ color: faint }}>5</Typography>
          <Typography variant="caption" sx={{ color: faint }}>10 (Dry)</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DroughtFactorControl;
